import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Container, Typography, TextField, Button, Paper, Alert,
  InputAdornment, CircularProgress, LinearProgress, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import { Phone, Lock, CheckCircle, Refresh, Email, Coffee } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { colors, designTokens } from '../../theme/theme';
import authService from '../../services/authService';
import { useTranslation } from 'react-i18next';

type Step = 'input' | 'otp' | 'success';
type Method = 'phone' | 'email';

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const isRegisterMode = searchParams.get('mode') === 'register';
  const navigate = useNavigate();
  const { verifyOTP } = useAuth();
  const { t } = useTranslation();

  const [step, setStep] = useState<Step>('input');
  const [method, setMethod] = useState<Method>('email');
  const [phone, setPhone] = useState('+994');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [successMsg, setSuccessMsg] = useState('');
  const [masked, setMasked] = useState('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isCounting = countdown > 0;
  useEffect(() => {
    if (!isCounting) return;
    const id = setInterval(() => setCountdown(c => {
      if (c <= 1) { clearInterval(id); return 0; }
      return c - 1;
    }), 1000);
    timerRef.current = id;
    return () => clearInterval(id);
  }, [isCounting]);

  const sendingRef = useRef(false);
  const handleSendOTP = async () => {
    if (sendingRef.current || loading) return;
    sendingRef.current = true;
    setError(''); setSuccessMsg(''); setLoading(true);

    try {
      let payload: any = {};
      if (method === 'phone') {
        const cleaned = phone.replace(/[\s\-().]/g, '');
        if (cleaned.length < 13 || !cleaned.startsWith('+994')) {
          setError('Please enter a valid Azerbaijan mobile number (+994 XX XXX XX XX)');
          setLoading(false); return;
        }
        payload = { phone: cleaned };
      } else {
        if (!email || !email.includes('@') || !email.includes('.')) {
          setError('Please enter a valid email address');
          setLoading(false); return;
        }
        payload = { email: email.trim().toLowerCase() };
      }

      const data = method === 'phone'
        ? await authService.requestOTP(payload.phone)
        : await authService.requestOTPByEmail(payload.email);
      setMasked(data.masked || '');
      setStep('otp');
      setCountdown(60);
      setSuccessMsg(method === 'phone'
        ? 'Verification code sent via SMS!'
        : 'Verification code sent to your email!');
    } catch (e: any) {
      console.warn('[LoginPage] OTP request failed', e);
      setError(typeof e.message === 'string' ? e.message : 'Failed to send code.');
    } finally { setLoading(false); sendingRef.current = false; }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;
    setError(''); setSuccessMsg(''); setLoading(true);
    try {
      if (method === 'phone') {
        await authService.requestOTP(phone.replace(/[\s\-().]/g, ''));
      } else {
        await authService.requestOTPByEmail(email.trim().toLowerCase());
      }
      setCountdown(60); setOtp('');
      setSuccessMsg('New code sent!');
    } catch (e: any) {
      console.warn('[LoginPage] OTP resend failed', e);
      setError(e.message || 'Failed to resend.');
    } finally { setLoading(false); }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) { setError('Please enter the full 6-digit code'); return; }
    setError(''); setSuccessMsg(''); setLoading(true);
    try {
      const key = method === 'phone' ? phone.replace(/[\s\-().]/g, '') : email.trim().toLowerCase();
      const result = await verifyOTP(key, otp);

      setStep('success');
      setTimeout(() => {
        if (result.is_new_user) { navigate('/register'); }
        else { navigate('/'); }
      }, 800);
    } catch (e: any) {
      console.warn('[LoginPage] OTP verify failed', e);
      const msg = typeof e.message === 'string' ? e.message : 'Invalid code.';
      setError(msg);
      if (msg.toLowerCase().includes('expired') || msg.toLowerCase().includes('request a new')) {
        setCountdown(0);
      }
    } finally { setLoading(false); }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 4,
        position: 'relative',
        overflow: 'hidden',
        bgcolor: colors.background.default,
      }}
    >
      {/* ── Dark ambient background with warm brown glow ── */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(ellipse 70% 60% at 25% 30%, rgba(180,100,60,0.18) 0%, transparent 65%),
            radial-gradient(ellipse 50% 50% at 75% 70%, rgba(138,92,246,0.08) 0%, transparent 60%),
            radial-gradient(ellipse 40% 40% at 50% 90%, rgba(212,88,106,0.06) 0%, transparent 50%),
            ${colors.background.default}
          `,
          zIndex: 0,
        }}
      />

      <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3.5, sm: 4.5 },
            borderRadius: 5,
            border: `1px solid ${designTokens.glass.border}`,
            bgcolor: designTokens.glass.surface,
            backdropFilter: designTokens.glass.blur,
          }}
        >
          {/* ── Brand Mark ── */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 16,
                background: colors.primary.gradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2.5,
                boxShadow: designTokens.shadow.glow,
              }}
            >
              <Coffee sx={{ fontSize: 28, color: 'white' }} />
            </Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: '#FFFFFF', mb: 0.5 }}
            >
              {isRegisterMode ? t('auth.create_account') : t('auth.welcome_back')}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.neutral[500] }}>
              {isRegisterMode
                ? 'Join CoffeeMates and meet people over great coffee'
                : t('auth.sign_in_subtitle')}
            </Typography>
          </Box>

          {loading && (
            <LinearProgress
              sx={{
                mb: 2,
                borderRadius: 4,
                bgcolor: 'rgba(255,255,255,0.06)',
                '& .MuiLinearProgress-bar': {
                  background: colors.primary.gradient,
                },
              }}
            />
          )}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {successMsg && !error && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}

          {/* ── Step 1: Input ── */}
          {step === 'input' && (
            <>
              <ToggleButtonGroup
                value={method}
                exclusive
                onChange={(_, v) => { if (v) { setMethod(v); setError(''); } }}
                sx={{ mb: 3, width: '100%' }}
              >
                <ToggleButton value="email" sx={{ flex: 1, py: 1 }}>
                  <Email sx={{ mr: 1, fontSize: 18 }} /> Email
                </ToggleButton>
                <ToggleButton value="phone" sx={{ flex: 1, py: 1 }}>
                  <Phone sx={{ mr: 1, fontSize: 18 }} /> Phone
                </ToggleButton>
              </ToggleButtonGroup>

              {method === 'phone' ? (
                <>
                  <Typography variant="body2" sx={{ color: colors.neutral[500], mb: 2 }}>
                    We'll send a verification code via SMS
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="+994 50 123 45 67"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone sx={{ color: colors.neutral[400], fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 3 }}
                    onKeyDown={(e) => e.key === 'Enter' && !loading && handleSendOTP()}
                    disabled={loading}
                  />
                </>
              ) : (
                <>
                  <Typography variant="body2" sx={{ color: colors.neutral[500], mb: 2 }}>
                    We'll send a verification code to your email
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="your@email.com"
                    value={email}
                    type="email"
                    onChange={(e) => setEmail(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email sx={{ color: colors.neutral[400], fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 3 }}
                    onKeyDown={(e) => e.key === 'Enter' && !loading && handleSendOTP()}
                    disabled={loading}
                  />
                </>
              )}

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleSendOTP}
                disabled={loading}
                sx={{ py: 1.5 }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : t('auth.send_code')}
              </Button>
            </>
          )}

          {/* ── Step 2: OTP ── */}
          {step === 'otp' && (
            <>
              <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600, color: '#FFFFFF' }}>
                {t('auth.enter_code')}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.neutral[500], mb: 3 }}>
                Sent to <strong style={{ color: colors.primary.main }}>{masked}</strong>
              </Typography>
              <TextField
                fullWidth
                placeholder="000000"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: colors.neutral[400], fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  maxLength: 6,
                  style: { letterSpacing: '0.5em', fontSize: '1.2rem', textAlign: 'center' },
                }}
                sx={{ mb: 3 }}
                onKeyDown={(e) => e.key === 'Enter' && !loading && handleVerifyOTP()}
                disabled={loading}
                autoFocus
              />
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleVerifyOTP}
                disabled={loading}
                sx={{ py: 1.5, mb: 2 }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : t('auth.verify')}
              </Button>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => { setStep('input'); setOtp(''); setError(''); setSuccessMsg(''); }}
                  sx={{ color: colors.neutral[500], fontSize: '0.85rem' }}
                >
                  {t('common.back')}
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleResendOTP}
                  disabled={countdown > 0 || loading}
                  startIcon={<Refresh sx={{ fontSize: 16 }} />}
                  sx={{
                    color: countdown > 0 ? colors.neutral[400] : colors.primary.main,
                    fontSize: '0.85rem',
                  }}
                >
                  {countdown > 0 ? t('auth.resend', { seconds: countdown }) : 'Resend code'}
                </Button>
              </Box>
            </>
          )}

          {/* ── Step 3: Success ── */}
          {step === 'success' && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 16,
                  bgcolor: colors.success.subtle,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <CheckCircle sx={{ fontSize: 32, color: colors.success.main }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFFFFF' }}>
                Verified!
              </Typography>
              <Typography variant="body2" sx={{ color: colors.neutral[500], mt: 0.5 }}>
                Taking you to your table...
              </Typography>
            </Box>
          )}
        </Paper>

        {/* ── Footer links ── */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" sx={{ color: colors.neutral[500] }}>
            {isRegisterMode ? 'Already have an account? ' : "Don't have an account? "}
            <Typography
              component="span"
              variant="body2"
              sx={{
                color: colors.primary.main,
                cursor: 'pointer',
                fontWeight: 600,
                '&:hover': { textDecoration: 'underline' },
              }}
              onClick={() => navigate(isRegisterMode ? '/login' : '/login?mode=register')}
            >
              {isRegisterMode ? t('auth.login') : t('auth.register')}
            </Typography>
          </Typography>
          <Typography variant="body2" sx={{ color: colors.neutral[400], mt: 1.5 }}>
            Coffee shop owner?{' '}
            <Typography
              component="span"
              variant="body2"
              sx={{
                color: colors.secondary.light,
                cursor: 'pointer',
                fontWeight: 600,
                '&:hover': { textDecoration: 'underline' },
              }}
              onClick={() => navigate('/partner/login')}
            >
              Partner Portal
            </Typography>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
